import React, { Component } from 'react'
import { Link, withRouter } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import { Table, Input } from 'antd'
import { SearchOutlined, CloseCircleOutlined } from '@ant-design/icons'

import { Colors } from './Styles'
import _ from 'lodash'
import { isMatch } from './utils/searchUtils'
import {
	setPersistentSearchQuery,
	getPersistentSearchQuery
} from '../utils/persistentSearchQueries'

class TableDisplay extends Component {
	state = {
		searchVisible: null,
		teamFilters: null,
		teamFilterVisible: false
	}

	didSelect(record, e) {
		const { onSelect, redirect, path, history, checkboxes } = this.props

		if (checkboxes) {
			// done elsewhere
		} else if (onSelect) {
			onSelect(record)
		} else if (redirect) {
			history.push(`${path}/${record._id}`)
		}
	}

	tableChanged = (pagination, filters, sorter) => {
		if (sorter.columnKey && sorter.order) {
			localStorage.setItem(
				this.props.sortKey,
				JSON.stringify({
					column: sorter.columnKey,
					order: sorter.order
				})
			)
		} else {
			localStorage.removeItem(this.props.sortKey)
		}
	}

	filterRecordContentBySearchQuery(records, query) {
		if (!query || query.length < 3) {
			return records
		}

		// when we filter nested records, we dont want to assign empty arrays
		if (_.isEmpty(records)) {
			return undefined
		}

		const filteredRecords = []
		for (let record of records) {
			const matchingChildren = this.filterRecordContentBySearchQuery(
				record.children,
				query
			)
			if (
				!_.isEmpty(matchingChildren) ||
				this.recordMatchSearchQuery(query, record)
			) {
				filteredRecords.push({
					...record,
					children: matchingChildren
				})
			}
		}

		return filteredRecords
	}

	recordMatchSearchQuery(query, record) {
		if (query.length < 3) {
			return true
		}

		// the table props tells us where to search
		let searchablesPropsContent = []
		if (this.props.searchProps) {
			searchablesPropsContent = this.props.searchProps(record)
		} else if (this.props.nameProp) {
			searchablesPropsContent = [this.props.nameProp(record)]
		}
		return isMatch(searchablesPropsContent, query)
	}

	render() {
		const {
			link,
			path,
			intl,
			renderName,
			nameProp,
			searchProps,
			searchNamePlaceholder,
			columns,
			dataSource,
			selectedId,
			sortKey,
			searchKey,
			collapsed
		} = this.props

		let savedSort, savedSortColumn
		const storedSort = localStorage.getItem(sortKey)
		if (storedSort) {
			savedSort = JSON.parse(storedSort)
			savedSortColumn = savedSort ? savedSort.column : null
		}

		// heavy, but required if we want to filter nested data source
		const searchQuery = getPersistentSearchQuery(searchKey)
		const filteredDataSource = this.filterRecordContentBySearchQuery(
			dataSource,
			searchQuery
		)

		let cols = []

		if (nameProp) {
			cols.push({
				title: intl.formatMessage({ id: 'name' }),
				key: 'name',
				defaultSortOrder:
					savedSortColumn && savedSortColumn === 'name' ?
						savedSort.order
					:	'ascend',
				render: (text, record, index) => {
					if (renderName) {
						return renderName(record)
					}
					if (link) {
						return (
							<Link to={`${path}/${record._id}`}>
								{nameProp(record)}
							</Link>
						)
					}
					return nameProp(record)
				},
				sorter: (a, b) => {
					let aa = nameProp(a) || ''
					let bb = nameProp(b) || ''
					return aa.localeCompare(bb)
				},
				filterIcon: (filtered) => (
					<SearchOutlined
						style={{
							color:
								!!searchQuery ?
									Colors.primary
								:	Colors.disabled.icon
						}}
					/>
				),
				filterDropdown: (props) => (
					<Input
						ref={(e) => (this.searchInput = e)}
						placeholder={intl.formatMessage({
							id: searchNamePlaceholder || 'actions.searchName'
						})}
						value={searchQuery}
						onChange={(e) => {
							setPersistentSearchQuery(searchKey, e.target.value)
							this.forceUpdate()
						}}
						onPressEnter={() =>
							this.setState({ searchVisible: false })
						}
						prefix={<SearchOutlined />}
						suffix={
							!!searchQuery ?
								<CloseCircleOutlined
									onClick={() => {
										setPersistentSearchQuery(searchKey, '')
										this.forceUpdate()
									}}
								/>
							:	null
						}
					/>
				),
				// if the search visible is not yet set but we have a search query
				// we want to show the search query to the user, so he knows the content is filtered
				filterDropdownVisible:
					this.state.searchVisible ?? !!searchQuery,
				filteredValue: [searchQuery],
				filtered: !!searchQuery,
				onFilter: (value, record) => {
					// the filtering is done in the render function to support nested records
					return true
				},
				onFilterDropdownVisibleChange: (visible) => {
					this.setState({ searchVisible: visible })
					if (visible) {
						setTimeout(() => {
							this.searchInput.focus()
						}, 200)
					}
				}
			})
		}

		for (let col of columns) {
			cols.push({
				defaultSortOrder:
					savedSortColumn && savedSortColumn === col.key ?
						savedSort.order
					:	null,
				...col
			})
		}

		return (
			<Table
				rowKey={this.props.rowKey || '_id'}
				columns={cols}
				dataSource={filteredDataSource}
				pagination={false}
				onRow={(record) => ({
					onClick: (e) => this.didSelect(record, e)
				})}
				rowClassName={(record) =>
					record[this.props.rowKey] === selectedId ?
						'table-row-selected'
					:	''
				}
				size="small"
				onChange={this.tableChanged}
				defaultExpandAllRows={!collapsed}
				locale={{
					emptyText: intl.formatMessage({
						id: this.props.emptyText || 'noResults'
					})
				}}
				rowSelection={
					this.props.checkboxes ?
						{
							type: 'checkbox',
							onSelect: (
								record,
								selected,
								selectedRows,
								nativeEvent
							) => this.props.onSelect(selectedRows),
							selectedRowKeys: this.props.selectedIds
						}
					:	null
				}
			/>
		)
	}
}

export default withRouter(injectIntl(TableDisplay))
